import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { holidaysApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Flag,
  MapPin,
  Star,
  Upload,
  FileSpreadsheet,
  Trash2,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HolidaysPage() {
  const { isHRManager } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    name: '',
    date: null,
    type: 'national'
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const fetchHolidays = async () => {
    try {
      const response = await holidaysApi.getAll(selectedYear);
      setHolidays(response.data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    
    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      await holidaysApi.create({
        name: formData.name,
        date: format(formData.date, 'yyyy-MM-dd'),
        type: formData.type
      });
      toast.success('Holiday added successfully');
      setShowAddModal(false);
      resetForm();
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/holidays/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(response.data.message);
      setShowUploadModal(false);
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload holidays');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/holidays/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete holiday');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      date: null,
      type: 'national'
    });
  };

  const downloadTemplate = () => {
    const csvContent = "name,date,type\nRepublic Day,2026-01-26,national\nPongal,2026-01-14,regional\n";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'holiday_template.csv';
    a.click();
    toast.info('Convert CSV to XLSX before uploading');
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'national':
        return <Badge className="bg-[#8B1E24] text-white border-0"><Flag className="h-3 w-3 mr-1" />National</Badge>;
      case 'regional':
        return <Badge className="bg-[#F5A623] text-white border-0"><MapPin className="h-3 w-3 mr-1" />Regional</Badge>;
      case 'optional':
        return <Badge variant="outline"><Star className="h-3 w-3 mr-1" />Optional</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 3 }, (_, i) => currentYear + i);
  };

  const upcomingHolidays = holidays.filter(h => new Date(h.date) >= new Date()).slice(0, 5);
  const monthGroups = holidays.reduce((acc, h) => {
    const month = new Date(h.date).toLocaleDateString('en-IN', { month: 'long' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(h);
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="holidays-page">
      {/* Header */}
      <div className="page-header flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="page-title">Holiday Calendar</h1>
          <p className="page-description">Public holidays and company observances for {selectedYear}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-32 rounded-xl">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {getYearOptions().map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isHRManager && (
            <>
              <Button 
                variant="outline"
                className="rounded-xl gap-2"
                onClick={() => setShowUploadModal(true)}
                data-testid="upload-holidays-btn"
              >
                <Upload className="h-4 w-4" />
                Upload Excel
              </Button>
              <Button 
                className="btn-primary rounded-xl gap-2"
                onClick={() => setShowAddModal(true)}
                data-testid="add-holiday-btn"
              >
                <Plus className="h-4 w-4" />
                Add Holiday
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Upcoming Holidays */}
      {upcomingHolidays.length > 0 && (
        <Card className="border-2 border-[#F5A623]/30 bg-gradient-to-br from-[#F5A623]/5 to-transparent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarIcon className="h-5 w-5 text-[#F5A623]" />
              Upcoming Holidays
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {upcomingHolidays.map((holiday, index) => (
                <div 
                  key={holiday.id} 
                  className={cn(
                    "flex items-center gap-4 p-4 rounded-2xl border transition-all hover:shadow-md",
                    index === 0 
                      ? "bg-gradient-to-br from-[#F5A623]/10 to-[#F5A623]/5 border-[#F5A623]/30" 
                      : "bg-card border-border"
                  )}
                >
                  <div className={cn(
                    "text-center min-w-[60px] p-3 rounded-xl",
                    index === 0 ? "bg-[#F5A623] text-white" : "bg-muted"
                  )}>
                    <p className="text-2xl font-bold">{new Date(holiday.date).getDate()}</p>
                    <p className="text-xs uppercase opacity-80">
                      {new Date(holiday.date).toLocaleDateString('en-IN', { month: 'short' })}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold">{holiday.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(holiday.date).toLocaleDateString('en-IN', { weekday: 'long' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Holidays by Month */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(monthGroups).map(([month, monthHolidays]) => (
          <Card key={month} className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-[#8B1E24]/5 to-transparent pb-3">
              <CardTitle className="text-base font-semibold">{month}</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {monthHolidays.map((holiday) => {
                  const date = new Date(holiday.date);
                  const isPast = date < new Date();
                  
                  return (
                    <div 
                      key={holiday.id}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-xl border transition-all",
                        isPast ? "opacity-50 bg-muted/30" : "hover:bg-muted/50 hover:shadow-sm"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-center min-w-[44px] py-1 px-2 rounded-lg bg-muted">
                          <p className="text-lg font-bold leading-tight">{date.getDate()}</p>
                          <p className="text-[10px] text-muted-foreground uppercase">
                            {date.toLocaleDateString('en-IN', { weekday: 'short' })}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{holiday.name}</p>
                          <div className="mt-1">{getTypeBadge(holiday.type)}</div>
                        </div>
                      </div>
                      {isHRManager && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDeleteHoliday(holiday.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {holidays.length === 0 && !loading && (
        <Card className="py-16 text-center">
          <CardContent>
            <CalendarIcon className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No holidays found for {selectedYear}</p>
            {isHRManager && (
              <p className="text-sm text-muted-foreground mt-2">Add holidays manually or upload an Excel file</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Holiday Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Add Holiday</DialogTitle>
            <DialogDescription>Add a new public holiday or company observance</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddHoliday} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Holiday Name</Label>
              <Input
                id="name"
                placeholder="e.g., Republic Day"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal rounded-xl",
                      !formData.date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date ? format(formData.date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.date}
                    onSelect={(date) => setFormData({ ...formData, date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(v) => setFormData({ ...formData, type: v })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national">National Holiday</SelectItem>
                  <SelectItem value="regional">Regional Holiday</SelectItem>
                  <SelectItem value="optional">Optional Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary rounded-xl">
                Add Holiday
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Upload Holidays from Excel</DialogTitle>
            <DialogDescription>
              Upload an Excel file with holiday data. Required columns: name, date
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div 
              className="border-2 border-dashed border-border rounded-2xl p-8 text-center cursor-pointer hover:border-[#8B1E24]/50 hover:bg-muted/30 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">Click to upload Excel file</p>
              <p className="text-sm text-muted-foreground mt-1">.xlsx or .xls files only</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>

            <div className="p-4 rounded-xl bg-muted/50">
              <p className="text-sm font-medium mb-2">Excel Format Required:</p>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>• Column A: <code className="bg-background px-1 rounded">name</code> (Holiday name)</p>
                <p>• Column B: <code className="bg-background px-1 rounded">date</code> (YYYY-MM-DD format)</p>
                <p>• Column C: <code className="bg-background px-1 rounded">type</code> (national/regional/optional)</p>
              </div>
              <Button variant="link" size="sm" className="mt-2 p-0 h-auto" onClick={downloadTemplate}>
                <Download className="h-3 w-3 mr-1" />
                Download template
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)} className="rounded-xl">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
